import { invokeIf } from '../utils/conditionalInvoke'
import { gaTracker } from '../utils/gaTracker'

type TrackingFnc<TResp, TParams, TTrackingObj extends {}> = (resp: TResp, req?: TParams) => TTrackingObj
type WrappedResponse<T> = { data: T }

type ApiFcn<TResp, TParams> = (arg: TParams) => Promise<WrappedResponse<TResp>>

interface ApiRequestOptions<TResp extends {}, TParams, TTrackData extends {} = {}> {
  trackResponse?: boolean
  skipFirstTracking?: boolean
  trackMapper?: TrackingFnc<TResp, TParams, TTrackData>
}

const defaultTrackMapper = <TResp>(d: TResp) => d

export const apiRequest = <TResp extends {}, TParams>(
  service: ApiFcn<TResp, TParams>,
  options: ApiRequestOptions<TResp, TParams> = {
    trackResponse: true,
    skipFirstTracking: false,
    trackMapper: defaultTrackMapper
  }
): ApiFcn<TResp, TParams> => {
  let enableTracking = !options.skipFirstTracking
  let initInteraction = !options.skipFirstTracking
  const trackMapping = options.trackMapper ?? defaultTrackMapper

  const trackResponse = (resp: TResp, req?: TParams) => {
    invokeIf(Boolean(options?.trackResponse) && enableTracking, () =>
      gaTracker.onInteraction(trackMapping(resp, req) as object, initInteraction)
    )
    if (!enableTracking) {
      enableTracking = true
    }
    if (initInteraction) {
      initInteraction = false
    }
  }

  return async (req: TParams = undefined as TParams) => {
    const resp = await service(req)
    trackResponse(resp.data, req)
    return resp
  }
}
